import React, { useEffect, useRef, useState } from 'react';
import { SimpleModulePage } from '../modules/SimpleModulePage';
import { config } from '../config/environment';

// ==========================================
// 타입 정의
// ==========================================
interface HealthDiary {
  id?: string;
  date: string;
  content: string;
  contentPreview: string;
  createdAt?: string;
  updatedAt?: string;
}

// ==========================================
// SectionCard 컴포넌트
// ==========================================
const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ 
  children, 
  className = "" 
}) => (
  <div className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${className}`}>
    {children}
  </div>
);

// ==========================================
// 인터페이스
// ==========================================
interface HealthDiaryPageProps {
  onBack?: () => void;
}

const HealthDiaryPage: React.FC<HealthDiaryPageProps> = ({ onBack }) => {
  // ==========================================
  // 상태 관리
  // ==========================================
  const [diaryData, setDiaryData] = useState<HealthDiary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedDiary, setSelectedDiary] = useState<HealthDiary | null>(null);
  const [selectedDateLoading, setSelectedDateLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);

  // ==========================================
  // 요일 계산: Zeller 공식 (순수 수학, 타임존 영향 없음)
  // ==========================================
  const calculateDayOfWeek = (year: number, month: number, day: number): string => {
    // Zeller's congruence formula for Gregorian calendar
    // Reference: https://en.wikipedia.org/wiki/Zeller%27s_congruence
    let m = month;
    let y = year;
    
    // January and February are counted as months 13 and 14 of the previous year
    if (month <= 2) {
      m += 12;
      y -= 1;
    }
    
    const K = y % 100; // Year of the century
    const J = Math.floor(y / 100); // Century
    
    // Formula: h = (q + floor(13(m+1)/5) + K + floor(K/4) + floor(J/4) - 2J) mod 7
    const h = (day + Math.floor(13 * (m + 1) / 5) + K + Math.floor(K / 4) + Math.floor(J / 4) - 2 * J) % 7;
    
    // Result mapping:
    // h=0(Saturday), h=1(Sunday), h=2(Monday), h=3(Tuesday), h=4(Wednesday), h=5(Thursday), h=6(Friday)
    // Convert to Korean day labels: 0=일(Sun), 1=월(Mon), 2=화(Tue), 3=수(Wed), 4=목(Thu), 5=금(Fri), 6=토(Sat)
    const dayOfWeek = (h + 6) % 7;
    const dayLabels = ['일', '월', '화', '수', '목', '금', '토'];
    
    console.log('[calculateDayOfWeek] Input:', { year, month, day, m, K, J, h, dayOfWeek, result: dayLabels[dayOfWeek] });
    
    return dayLabels[dayOfWeek];
  };

  // ==========================================
  // 날짜를 YYYY년 MM월 DD일 (요일) 형식으로 포맷팅
  // ==========================================
  const formatDateWithDay = (dateStr: string): string => {
    if (!dateStr) return '날짜 없음';
    
    try {
      const trimmed = String(dateStr).trim();
      
      // ISO 8601 형식에서 YYYY-MM-DD 부분만 추출
      let dateOnly = trimmed;
      if (trimmed.includes('T')) {
        dateOnly = trimmed.split('T')[0];
      }
      
      // YYYY-MM-DD 형식 검증
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
        console.warn('[formatDateWithDay] Invalid format:', dateOnly);
        return dateOnly;
      }
      
      // YYYY-MM-DD를 파싱
      const [year, month, day] = dateOnly.split('-').map(Number);
      
      // 요일 계산
      const dayOfWeek = calculateDayOfWeek(year, month, day);
      
      // 포맷팅: YYYY년 MM월 DD일 (요일)
      const formatted = `${year}년 ${String(month).padStart(2, '0')}월 ${String(day).padStart(2, '0')}일 (${dayOfWeek})`;
      console.log('[formatDateWithDay] Formatted:', dateOnly, '->', formatted);
      
      return formatted;
    } catch (error) {
      console.error('[formatDateWithDay] Error:', error, 'input:', dateStr);
      return dateStr || '날짜 오류';
    }
  };

  // ==========================================
  // KST 기준 오늘 날짜 조회 (백엔드에서 받음)
  // ==========================================
  const getTodayInKST = async (): Promise<string> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return '';

      const response = await fetch(`${config.apiUrl}/health-diary/today`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const todayDate = data.todayDate;
        console.log('[getTodayInKST] Backend returned:', todayDate);
        return todayDate;
      }
    } catch (error) {
      console.error('[getTodayInKST] Error:', error);
    }
    return '';
  };

  // ==========================================
  // 건강 일기 목록 로드
  // ==========================================
  const loadHealthDiaries = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('[HealthDiary] No auth token found');
        setLoading(false);
        return;
      }

      // 백엔드에서 오늘 날짜 조회
      const todayStr = await getTodayInKST();
      if (!todayStr) {
        console.error('[HealthDiary] Failed to get today date');
        setLoading(false);
        return;
      }

      console.log('[HealthDiary] Today (from backend):', todayStr);

      const rangeSize = 365;
      const startDaysOffset = offset * rangeSize + rangeSize;
      const endDaysOffset = offset * rangeSize;

      // 날짜 문자열 파싱 (YYYY-MM-DD)
      const [year, month, day] = todayStr.split('-').map(Number);

      // 시작 날짜 계산 (startDaysOffset일 전)
      let startYear = year;
      let startMonth = month;
      let startDay = day - startDaysOffset;

      while (startDay <= 0) {
        startMonth -= 1;
        if (startMonth <= 0) {
          startMonth = 12;
          startYear -= 1;
        }
        // 달의 마지막 날짜 계산
        const daysInPrevMonth = [31, (startYear % 4 === 0 && startYear % 100 !== 0) || startYear % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][startMonth - 1];
        startDay += daysInPrevMonth;
      }

      // 종료 날짜 계산 (endDaysOffset일 전)
      let endYear = year;
      let endMonth = month;
      let endDay = day - endDaysOffset;

      while (endDay <= 0) {
        endMonth -= 1;
        if (endMonth <= 0) {
          endMonth = 12;
          endYear -= 1;
        }
        const daysInPrevMonth = [31, (endYear % 4 === 0 && endYear % 100 !== 0) || endYear % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][endMonth - 1];
        endDay += daysInPrevMonth;
      }

      const startDateStr = `${startYear}-${String(startMonth).padStart(2, '0')}-${String(startDay).padStart(2, '0')}`;
      const endDateStr = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;

      const isFirstLoad = offset === 0;
      if (isFirstLoad) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      console.log('[HealthDiary] Fetching range (KST):', { startDate: startDateStr, endDate: endDateStr });

      const response = await fetch(
        `${config.apiUrl}/health-diary/range?startDate=${startDateStr}&endDate=${endDateStr}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      console.log('[HealthDiary] Raw API response:', data);

      if (data.success && data.data) {
        const allDiaries = data.data;
        console.log('[HealthDiary] Diaries count:', allDiaries.length);
        
        // 데이터 검증 - 첫 번째 항목의 전체 구조 확인
        if (allDiaries.length > 0) {
          console.log('[HealthDiary] First diary full object:', JSON.stringify(allDiaries[0], null, 2));
        }
        
        allDiaries.forEach((diary: any, idx: number) => {
          console.log(`[HealthDiary] Diary ${idx}:`, {
            date: diary.date,
            diaryDate: diary.diaryDate,
            createdAt: diary.createdAt,
            updatedAt: diary.updatedAt,
            content: diary.content?.substring(0, 30) || 'N/A'
          });
        });
        
        const pageSize = 10;
        const pagedData = allDiaries.slice(0, pageSize);

        if (isFirstLoad) {
          setDiaryData(pagedData);
        } else {
          setDiaryData(prev => [...prev, ...pagedData]);
        }

        setHasMore(allDiaries.length >= pageSize);
      } else {
        console.warn('[HealthDiary] Invalid response format:', data);
        if (isFirstLoad) {
          setDiaryData([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('[HealthDiary] Error loading diaries:', error);
      setHasMore(false);
    } finally {
      if (offset === 0) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  // ==========================================
  // 초기 로드
  // ==========================================
  useEffect(() => {
    loadHealthDiaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  // ==========================================
  // IntersectionObserver - 무한 스크롤
  // ==========================================
  useEffect(() => {
    const currentTarget = observerTarget.current;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore && !selectedDate) {
          setOffset(prev => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, loadingMore, selectedDate]);

  // ==========================================
  // 날짜 선택 - 상세 일기 로드
  // ==========================================
  const handleDateClick = async (date: string) => {
    setSelectedDateLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('[HealthDiary] No auth token found');
        throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
      }

      const trimmedDate = date.trim();
      
      // ISO 8601 형식 (2026-03-23T15:00:00.000Z)에서 YYYY-MM-DD 부분만 추출
      const dateOnly = trimmedDate.includes('T') ? trimmedDate.split('T')[0] : trimmedDate;
      console.log('[HealthDiary] Loading diary for date:', dateOnly);
      
      const apiUrl = `${config.apiUrl}/health-diary/date/${encodeURIComponent(dateOnly)}`;
      const response = await fetch(apiUrl, {
        cache: 'no-store',  // 캐시 사용 불가
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
        },
      });

      console.log('[HealthDiary] Response status:', response.status);

      // 304 (Not Modified) 상태 처리 - 캐시된 응답이므로 본문이 없음
      if (response.status === 304) {
        console.warn('[HealthDiary] Received 304 Not Modified - cache issue');
        throw new Error('캐시 데이터 오류. 새로고침 후 다시 시도해주세요.');
      }

      // 200만 정상 응답으로 처리
      if (response.status !== 200) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      console.log('[HealthDiary] Diary data:', data);
      console.log('[HealthDiary] Response keys:', Object.keys(data || {}));
      
      // API 응답이 여러 형식을 지원
      let diaryData = null;
      if (data.data) {
        diaryData = data.data;
      } else if (data.content !== undefined) {
        // 직접 일기 데이터가 반환된 경우
        diaryData = data;
      } else if (Array.isArray(data) && data.length > 0) {
        // 배열 형식 응답
        diaryData = data[0];
      } else if (data.success && data.data === null) {
        // API가 성공했지만 데이터가 없는 경우 - 빈 일기 생성
        console.log('[HealthDiary] No diary exists for this date, creating empty entry');
        diaryData = {
          date: dateOnly,
          content: '',
          contentPreview: '',
        };
      }
      
      if (diaryData) {
        console.log('[HealthDiary] Setting selected diary and date with:', diaryData);
        setSelectedDiary(diaryData);
        setEditContent(diaryData.content || '');
        setIsEditing(false);
        // dateOnly (YYYY-MM-DD)를 저장해서 formatDate에서 문제 없도록
        setSelectedDate(dateOnly);
      } else {
        console.warn('[HealthDiary] No diary data found in response');
        throw new Error('응답 데이터가 없습니다');
      }
    } catch (error) {
      console.error('[HealthDiary] Error loading diary detail:', error);
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류';
      console.error('[HealthDiary] Error details:', { message: errorMessage, error });
      alert(`건강일기를 불러올 수 없습니다.\n${errorMessage}\n\n개발자 도구를 확인해주세요.`);
      setSelectedDate(null);
      setSelectedDiary(null);
    } finally {
      setSelectedDateLoading(false);
    }
  };

  // ==========================================
  // 편집 시작
  // ==========================================
  const handleEditStart = () => {
    setIsEditing(true);
    setEditContent(selectedDiary?.content || '');
  };

  // ==========================================
  // 편집 취소
  // ==========================================
  const handleEditCancel = () => {
    setIsEditing(false);
    setEditContent(selectedDiary?.content || '');
  };

  // ==========================================
  // 저장
  // ==========================================
  const handleSave = async () => {
    if (!editContent.trim()) {
      alert('일기 내용을 입력해주세요.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${config.apiUrl}/health-diary/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: editContent.trim(),
          diaryDate: selectedDate,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSelectedDiary(data.data);
          setIsEditing(false);
          
          // 목록 업데이트
          setDiaryData(prev =>
            prev.map(d =>
              d.date === selectedDate
                ? {
                    ...d,
                    content: editContent.trim(),
                    contentPreview: editContent.trim().substring(0, 50),
                  }
                : d
            )
          );
        }
      }
    } catch (error) {
      console.error('[HealthDiary] Error saving diary:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  // ==========================================
  // 삭제
  // ==========================================
  const handleDelete = async () => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${config.apiUrl}/health-diary/${selectedDiary?.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setDiaryData(prev => prev.filter(d => d.date !== selectedDate));
        setSelectedDate(null);
        setSelectedDiary(null);
      }
    } catch (error) {
      console.error('[HealthDiary] Error deleting diary:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // ==========================================
  // 렌더링 - 상세 화면
  // ==========================================
  console.log('[HealthDiaryPage] Render check - selectedDate:', selectedDate, 'selectedDiary:', selectedDiary);
  
  if (selectedDate && selectedDiary) {
    console.log('[HealthDiaryPage] Rendering detail page for:', selectedDate);
    return (
      <SimpleModulePage
        title="건강 일기"
        subtitle={formatDateWithDay(selectedDate)}
        icon="📔"
        onBack={() => {
          setSelectedDate(null);
          setSelectedDiary(null);
          setIsEditing(false);
        }}
      >
        {selectedDateLoading ? (
          <SectionCard>
            <p className="text-center text-[12px] text-slate-500">로드 중...</p>
          </SectionCard>
        ) : isEditing ? (
          <SectionCard>
            <div className="mb-3">
              <p className="text-[13px] font-semibold text-slate-900 mb-2">
                {formatDateWithDay(selectedDate)}
              </p>
              <textarea
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                placeholder="건강 일기를 작성해주세요..."
                maxLength={2000}
                className="w-full p-3 border border-slate-200 rounded-lg text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-violet-500"
                style={{ minHeight: '300px' }}
              />
              <p className="text-[11px] text-slate-500 mt-2 text-right">
                {editContent.length} / 2000
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="flex-1 py-2.5 px-3 bg-violet-600 text-white text-[13px] font-semibold rounded-lg hover:bg-violet-700 transition"
              >
                저장
              </button>
              <button
                onClick={handleEditCancel}
                className="flex-1 py-2.5 px-3 bg-slate-200 text-slate-900 text-[13px] font-semibold rounded-lg hover:bg-slate-300 transition"
              >
                취소
              </button>
            </div>
          </SectionCard>
        ) : (
          <SectionCard>
            <div className="mb-4">
              <p className="text-[13px] font-semibold text-slate-900 mb-3">
                {formatDateWithDay(selectedDate)}
              </p>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-[13px] text-slate-900 leading-relaxed whitespace-pre-wrap">
                  {selectedDiary.content || '내용 없음'}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleEditStart}
                className="flex-1 py-2.5 px-3 bg-blue-600 text-white text-[13px] font-semibold rounded-lg hover:bg-blue-700 transition"
              >
                수정
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-2.5 px-3 bg-red-600 text-white text-[13px] font-semibold rounded-lg hover:bg-red-700 transition"
              >
                삭제
              </button>
            </div>
          </SectionCard>
        )}
      </SimpleModulePage>
    );
  }

  // ==========================================
  // 렌더링 - 리스트 화면
  // ==========================================
  return (
    <SimpleModulePage
      title="건강 일기"
      subtitle="일기 기록"
      icon="📔"
      onBack={onBack}
    >
      {loading ? (
        <SectionCard>
          <p className="text-center text-[13px] text-slate-600">로딩 중...</p>
        </SectionCard>
      ) : diaryData.length === 0 ? (
        <SectionCard>
          <p className="text-center text-[13px] text-slate-600">
            아직 건강 일기가 없습니다.
          </p>
        </SectionCard>
      ) : (
        <SectionCard>
          <div className="space-y-0">
            {diaryData.map((diary, idx) => {
              // date가 유효한지 확인 (ISO 8601 또는 YYYY-MM-DD 형식)
              const dateStr = diary?.date;
              const isValidDate = dateStr && /^\d{4}-\d{2}-\d{2}/.test(String(dateStr).trim());
              
              return (
                <button
                  key={diary.date || idx}
                  onClick={() => isValidDate && handleDateClick(dateStr)}
                  disabled={selectedDateLoading || !isValidDate}
                  className={`w-full py-3 px-0 text-left border-b border-slate-100 ${
                    idx === diaryData.length - 1 ? 'last:border-b-0' : ''
                  } hover:bg-slate-50 transition-colors flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <div className="flex-1">
                    <p className="text-[13px] font-semibold text-slate-900 mb-1">
                      {isValidDate ? formatDateWithDay(dateStr) : '날짜 오류'}
                    </p>
                    <p className="text-[11px] text-slate-600 line-clamp-1">
                      {diary.contentPreview || '내용 없음'}
                    </p>
                  </div>
                  <span className="text-slate-400 ml-2">→</span>
                </button>
              );
            })}
          </div>

          {/* ✅ 스크롤 감지 대상 */}
          {hasMore && (
            <div
              ref={observerTarget}
              className="w-full py-4 flex justify-center"
            >
              {loadingMore && (
                <p className="text-[12px] text-slate-500">추가 데이터 로드 중...</p>
              )}
            </div>
          )}
        </SectionCard>
      )}

      {/* 안내 */}
      <SectionCard className="bg-blue-50 border-blue-200">
        <div className="flex gap-2">
          <span className="text-[16px] flex-shrink-0">ℹ️</span>
          <div>
            <p className="text-[12px] font-semibold text-blue-900 mb-1">정보 안내</p>
            <p className="text-[11px] text-blue-800 leading-relaxed">
              일자별 건강 일기를 최대 10개씩 표시합니다. 하단으로 스크롤하면 추가 데이터를 자동으로 로드합니다.
            </p>
          </div>
        </div>
      </SectionCard>
    </SimpleModulePage>
  );
};

export default HealthDiaryPage;
